import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { notEmpty } from "shared/lib/utils";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { FallbackReason, WidgetVersion } from "scmp-app/pages/home/ab-widget-fetch";
import type { seriesWidgetTabContentHomeV2Article$key } from "scmp-app/queries/__generated__/seriesWidgetTabContentHomeV2Article.graphql";

import {
  BottomDivider,
  BottomItem,
  BottomRow,
  Container,
  FullDivider,
  HeroImageLink,
  SCMPSeriesLink,
  SeriesRow,
  SeriesShortLabelLink,
  StyledContentCoverImage,
  StyledSecondaryItem,
  StyledSeriesPrimary,
} from "./styles";

type Props = {
  className?: string;
  fallbackReason?: FallbackReason;
  listOfArticles: string;
  order: number;
  reference: seriesWidgetTabContentHomeV2Article$key;
  widgetVersion?: WidgetVersion;
};

export const SeriesWidgetTabContent: FunctionComponent<Props> = ({
  className,
  fallbackReason,
  listOfArticles,
  order,
  reference,
  widgetVersion,
}) => {
  const content = useFragment(
    graphql`
      fragment seriesWidgetTabContentHomeV2Article on Article {
        seriesShortLabel
        entityId
        urlAlias
        ...entityLink
        series {
          ... on Content {
            entityId
            urlAlias
            publishedDate
            ...entityLink
            ...homeSecondaryV2ContentItemContent
            ...contentCoverImageContent @arguments(withLandscape250x99: true, withSize540x360: true)
          }
        }
      }
    `,
    reference,
  );

  const [head, ...rest] = [...(content?.series ?? [])].filter(notEmpty).toSorted((a, b) => {
    const dateA = new Date(a?.publishedDate ?? 0).getTime();
    const dateB = new Date(b?.publishedDate ?? 0).getTime();
    return dateB - dateA;
  });
  const restTwo = rest.slice(0, 2);

  if (!content || !content.seriesShortLabel || !head) return null;

  const label = content.seriesShortLabel.replaceAll(" ", "_").toLowerCase();

  const handleClick = (entityId?: string, destinationUrl?: string) => {
    sendGA4Tracking({
      action: "click",
      category: "recirculation",
      customized_parameters: {
        article_id: entityId,
        block_section: label,
        destination_url: destinationUrl,
        list_of_articles: listOfArticles,
        page_type: "homepage",
        recommendation_id: "",
        result: fallbackReason ? "error" : "valid",
        ...(fallbackReason && { result_error: fallbackReason }),
        ...(widgetVersion && { widget_version: widgetVersion }),
        widget_name: "scmp_series",
        widget_tab: order,
      },
      subcategory: "widget",
    });
  };

  return (
    <BaseLinkContextProvider customQueryParameters={{ module: `series_${label}` }}>
      <Container className={className}>
        <HeroImageLink onClick={() => handleClick(head.entityId, head.urlAlias)} reference={head}>
          <StyledContentCoverImage
            reference={head}
            responsiveVariants={{
              desktopUp: "landscape250x99",
              mobileUp: "size540x360",
              tabletUp: "landscape250x99",
            }}
          />
        </HeroImageLink>
        <SeriesRow>
          <SCMPSeriesLink as="/series" pathname="/series">
            SCMP Series
          </SCMPSeriesLink>
          <SeriesShortLabelLink
            onClick={() => handleClick(content.entityId, content.urlAlias)}
            reference={content}
          >
            {content.seriesShortLabel}
          </SeriesShortLabelLink>
        </SeriesRow>
        <StyledSeriesPrimary
          isReadingTimeEnabled
          onClick={(entityId, urlAlias) => handleClick(entityId, urlAlias)}
          reference={head}
          withComment
          withSummary
        />
        {restTwo.length > 0 && (
          <>
            <FullDivider />
            <BottomRow>
              {restTwo[0] && (
                <BottomItem>
                  <StyledSecondaryItem
                    isReadingTimeEnabled
                    onClick={(entityId, urlAlias) => handleClick(entityId, urlAlias)}
                    reference={restTwo[0]}
                    withComment
                  />
                </BottomItem>
              )}
              {restTwo[0] && restTwo[1] && <BottomDivider />}
              {restTwo[1] && (
                <BottomItem>
                  <StyledSecondaryItem
                    isReadingTimeEnabled
                    onClick={(entityId, urlAlias) => handleClick(entityId, urlAlias)}
                    reference={restTwo[1]}
                    withComment
                  />
                </BottomItem>
              )}
            </BottomRow>
          </>
        )}
      </Container>
    </BaseLinkContextProvider>
  );
};

SeriesWidgetTabContent.displayName = "SeriesWidgetTabContent";
